import { Box, Button, Dialog, useTheme } from "@mui/material"
import CloseModalButton from "../../../../components/CloseModalButton"
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from "../../../../components/Dialog.styled"
import { Autoclave, AutoclaveType } from "../types"
import InfoIcon from "@mui/icons-material/Info"
import constants from "../../../../constants"
import { ReactNode } from "react"
import barcodeImage from "../../../../images/barcode2.jpg"
import { AutoclaveTypeId } from "../enums"
import { getAutoclaveTypeName } from "../utils/helpers"
import useCustomer from "../../../../hooks/useCustomer"

type EnvelopeLineItem = {
  color: string,
  title: string,
  children: ReactNode,
}

type EnvelopeInstructionsModal = {
  open: boolean,
  autoclave?: Autoclave,
  autoclaveTypeById: Map<AutoclaveTypeId, AutoclaveType>,
  handleClose: () => void,
}

const EnvelopeLineItem = (props: EnvelopeLineItem) => {
  const { color, title, children } = props

  const theme = useTheme()

  return (
    <Box className="flex envelope-line-item" sx={{ columnGap: '4px' }}>
      <Box sx={{ color: theme.palette.common.black }}>{`${title}:`}</Box>
      <Box sx={{ color, textTransform: 'uppercase' }}>
        { children }
      </Box>
    </Box>    
  )
}

const EnvelopeInstructionsModal = (props: EnvelopeInstructionsModal) => {
  const { open, autoclave, autoclaveTypeById, handleClose } = props

  const theme = useTheme()

  const customer = useCustomer()

  const autoclaveTypeName = getAutoclaveTypeName(autoclave ?? null, autoclaveTypeById)

  return customer && autoclave && (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth={true}
      >
        <CloseModalButton handleClose={handleClose} />
        <StyledDialogTitle>
          <Box className="flex flex-align-center c-g-8">
            <InfoIcon fontSize="small" />
            <span>How To Fill Out My Envelope</span>
          </Box>
        </StyledDialogTitle>
        <StyledDialogContent>
          <Box sx={{ textAlign: 'center', marginBottom: '16px' }}>
            <Box sx={{ fontSize: theme.typography.htmlFontSize, color: theme.palette.primary.main }}>
              Customer Sterilization Information
            </Box>
            <Box sx={{ fontSize: theme.typography.fontSize, color: theme.palette.common.black  }}>
              Not responsible for envelopes not processed due to incomplete information.
            </Box>
          </Box>
          <Box className="flex flex-center">
            <Box sx={{ border: 1, borderColor: theme.palette.common.black, width: '500px' }}>
              <Box className="envelope-instructions-header">
                Required Information Needed on Every Test
              </Box>
              <Box>
                <EnvelopeLineItem title="Phone" color={theme.palette.error.main}>
                  { customer.phone ?? '' }
                </EnvelopeLineItem>
                <EnvelopeLineItem title="Customer ID" color={theme.palette.error.main}>
                  { customer.customer_number ?? '' }
                </EnvelopeLineItem>
                <EnvelopeLineItem title="Sterilizer #" color={theme.palette.error.main}>
                  { autoclave.autoclave_serial ?? '' }
                </EnvelopeLineItem>
                <Box className="envelope-line-item">
                  The sterilizer # identiifes the sertilizer on yout test report.
                </Box>
                <EnvelopeLineItem title="Sterilizer Brand" color={theme.palette.error.main}>
                  { autoclave.autoclave_brand ?? '' }
                </EnvelopeLineItem>
                <EnvelopeLineItem title="Test Date" color={theme.palette.grey[600]}>
                  &lt; MM/DD/YYYY &gt;
                </EnvelopeLineItem>
                <EnvelopeLineItem title="Type" color={theme.palette.error.main}>
                  { autoclaveTypeName }
                </EnvelopeLineItem>
                <Box className="envelope-line-item" sx={{ color: theme.palette.primary.main }}>
                  Please follow instructions found on the box.
                </Box>
                <Box className="envelope-line-item">
                  Caution: The packaging of this product contains Dry Natural Rubber Latex.
                </Box>
                <Box className="envelope-line-item">
                  May cause allergic reactions.
                </Box>
                <Box className="envelope-line-item" sx={{ color: theme.palette.primary.main }}>
                  OnTraq™: 1.800.331.1984
                </Box>
                <Box className="envelope-line-item" sx={{ color: theme.palette.primary.main }}>
                  Testing Consultant: 1.800.819.3336
                </Box>
                <Box className="envelope-line-item" sx={{ color: theme.palette.primary.main }}>
                  { constants.HEALTHFIRST_WEB_URL }
                </Box>
              </Box>
            </Box>
            <Box className="flex" sx={{ flexDirection: 'column', border: 1, borderLeft: 0, borderColor: theme.palette.common.black, width: '300px' }}>
              <Box sx={{ borderBottom: 1, borderColor: theme.palette.common.black }}>
                <Box className="envelope-instructions-header">
                  Optional Information
                </Box>
                <Box>
                  <EnvelopeLineItem title="Model #" color={theme.palette.error.main}>
                    { autoclave.autoclave_model ?? '' }
                  </EnvelopeLineItem>
                  <EnvelopeLineItem title="Operator" color={theme.palette.grey[600]}>
                    {''}
                  </EnvelopeLineItem>
                  <EnvelopeLineItem title="Test Time" color={theme.palette.grey[600]}>
                    &lt; Test Time &gt;
                  </EnvelopeLineItem>
                  <EnvelopeLineItem title="Temp" color={theme.palette.grey[600]}>
                    &lt; Temperature &gt;
                  </EnvelopeLineItem>
                  <EnvelopeLineItem title="Pressure" color={theme.palette.common.black}>
                    (PSI/kPA)_____________
                  </EnvelopeLineItem>
                </Box>
              </Box>
              <Box>
                <Box className="envelope-instructions-header">
                  New Customer? Change of Address?
                  <br/>
                  Please complete
                </Box>
                <Box>
                  <EnvelopeLineItem title="Office Name" color={theme.palette.error.main}>
                    { customer.name ?? '' }
                  </EnvelopeLineItem>
                  <EnvelopeLineItem title="Address" color={theme.palette.error.main}>
                    { customer.street ?? '' }
                  </EnvelopeLineItem>
                  <EnvelopeLineItem title="City" color={theme.palette.error.main}>
                    { customer.city ?? '' }
                  </EnvelopeLineItem>
                  <Box className="flex" sx={{ columnGap: '16px' }}>
                    <EnvelopeLineItem title="State" color={theme.palette.error.main}>
                      { customer.state ?? '' }
                    </EnvelopeLineItem>
                    <EnvelopeLineItem title="Zip" color={theme.palette.error.main}>
                      { customer.zip ?? '' }
                    </EnvelopeLineItem>
                  </Box>
                  <EnvelopeLineItem title="Fax" color={theme.palette.error.main}>
                    { customer.fax ?? '' }
                  </EnvelopeLineItem>
                  <EnvelopeLineItem title="Email" color={theme.palette.grey[600]}>
                    { customer.email ?? '' }
                  </EnvelopeLineItem>
                  <EnvelopeLineItem title="Contact Name" color={theme.palette.grey[600]}>
                    {''}
                  </EnvelopeLineItem>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box className="flex flex-center" sx={{ marginTop: '16px' }}>
            <Box className="flex" sx={{ flexDirection: 'column', fontSize: '10px', width: '500px', color: theme.palette.common.black }}>
              <Box className="flex" sx={{ columnGap: '16px' }}>
                <Box>Lot# XXXXXXX</Box>
                <Box>EXP XX/XXXX</Box>
              </Box>
              <Box>CVTXXXHFXXXXXXXXXXXX</Box>
            </Box>
            <Box sx={{ width: '300px' }}>
              <img height="30" width="250" src={barcodeImage} alt="barcode-image" />
            </Box>
          </Box>
        </StyledDialogContent>
        <StyledDialogFooter>
          <Button variant="contained" onClick={handleClose}>
            OK
          </Button>
        </StyledDialogFooter>
      </Dialog>
    </>
  )  
}

export default EnvelopeInstructionsModal
